
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { checkForUpdates, handleAppUpdate, reloadApplication, APP_VERSION } from '@/utils/versionControl';
import { toast } from 'sonner';

interface UpdateNotificationProps {
  autoReloadDelay?: number; // Delay in seconds before auto reload
}

const UpdateNotification = ({ autoReloadDelay = 5 }: UpdateNotificationProps) => {
  const [updateDetected, setUpdateDetected] = useState<boolean>(false);
  const [countdown, setCountdown] = useState<number>(autoReloadDelay);

  useEffect(() => {
    // Check for updates when component mounts
    const hasUpdate = checkForUpdates();
    setUpdateDetected(hasUpdate);
    
    if (hasUpdate) {
      toast.info("Application update available", {
        description: "Please refresh the application to get the latest version."
      });
      
      // Start countdown for auto-reload
      let timer: number | undefined;
      if (autoReloadDelay > 0) {
        timer = window.setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              // Handle update and reload
              handleAppUpdate();
              reloadApplication();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
      
      // Clean up interval on unmount
      return () => {
        if (timer) clearInterval(timer);
      };
    }
  }, [autoReloadDelay]);

  const handleUpdateNow = () => {
    handleAppUpdate();
    reloadApplication();
  };

  if (!updateDetected) {
    return null;
  }

  return (
    <Dialog open={updateDetected} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Application Update Available</DialogTitle>
          <DialogDescription>
            A new version (v{APP_VERSION}) of the application is available. Please update to the latest version.
            The application will automatically reload in {countdown} seconds.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="sm:justify-center">
          <Button onClick={handleUpdateNow} className="w-full sm:w-auto">
            Update Now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateNotification;
